import { markServerReachable, markServerUnreachable } from '../../offline/connectivity';
import { offlineRequestRuntime } from '../../offline/runtime';
import { getActiveLocale } from '../i18n/i18n';
import { configureRecaptcha, executeRecaptcha, preloadRecaptcha } from '../security/recaptcha';
import { ApiRequestError, isNetworkFailure } from './errors';

const API_BASE = '/api/v1';

/** The header the API reads a reCAPTCHA token from. Kept out of request bodies so no contract changes to gain protection. */
const RECAPTCHA_HEADER = 'X-Recaptcha-Token';

interface ProblemDetails {
  title?: string;
  detail?: string;
  code?: string;
}

// Re-exported so the many modules that already import the error from here keep working, and so
// there is one class for `instanceof` to compare against.
export {
  ApiRequestError,
  isNetworkFailure,
  isOfflineQueued,
  isOfflineUnavailable,
  OfflineQueuedError,
  OfflineUnavailableError,
} from './errors';

let requestToken: string | undefined;
let requestTokenPromise: Promise<string> | undefined;

function createCorrelationId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `web-${Date.now().toString(36)}`;
}

function isMutation(method: string | undefined): boolean {
  const normalized = (method ?? 'GET').toUpperCase();
  return normalized !== 'GET' && normalized !== 'HEAD' && normalized !== 'OPTIONS';
}

function createHeaders(init: RequestInit): Headers {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  headers.set('Accept-Language', getActiveLocale());
  headers.set('X-Correlation-ID', createCorrelationId());

  if (typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  return headers;
}

async function readProblem(response: Response): Promise<ProblemDetails | undefined> {
  try {
    return await response.json() as ProblemDetails;
  } catch {
    return undefined;
  }
}

async function fetchCsrfToken(): Promise<string> {
  const response = await fetch(`${API_BASE}/auth/csrf`, {
    method: 'GET',
    headers: createHeaders({}),
    credentials: 'include',
  });

  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiRequestError(
      response.status,
      problem?.detail ?? problem?.title ?? `API request failed with status ${response.status}.`,
      problem?.code,
    );
  }

  const payload = await response.json() as { requestToken?: unknown };
  if (typeof payload.requestToken !== 'string' || payload.requestToken.length === 0) {
    throw new ApiRequestError(500, 'The API returned an invalid anti-forgery token.');
  }

  requestToken = payload.requestToken;
  return payload.requestToken;
}

export function ensureCsrfToken(): Promise<string> {
  if (requestToken !== undefined) return Promise.resolve(requestToken);
  if (requestTokenPromise !== undefined) return requestTokenPromise;

  requestTokenPromise = fetchCsrfToken().finally(() => {
    requestTokenPromise = undefined;
  });
  return requestTokenPromise;
}

export function clearCsrfToken(): void {
  requestToken = undefined;
  requestTokenPromise = undefined;
}

interface RecaptchaProtectedRoute {
  method: string;
  path: string;
  action: string;
}

interface ClientConfiguration {
  recaptcha?: {
    enabled?: boolean;
    siteKey?: string | null;
    scriptHost?: string | null;
    protectedRoutes?: readonly RecaptchaProtectedRoute[];
  };
}

let protectedRoutes: readonly RecaptchaProtectedRoute[] = [];
let configurationPromise: Promise<void> | undefined;
let configurationLoaded = false;
let recaptchaDisabled = false;

/**
 * Takes this browser out of reCAPTCHA entirely: no script is loaded from Google, no token is
 * minted, and no request carries one — whatever the API publishes.
 *
 * It is a client decision and only a client decision. The guard that matters lives in the API,
 * which refuses a protected route whose token is missing, so this cannot weaken the server and
 * a shell that calls it can only be refused, never let through. Call it before the first render
 * so nothing has had a chance to arm itself in the meantime.
 */
export function disableRecaptcha(): void {
  recaptchaDisabled = true;
  protectedRoutes = [];
  configureRecaptcha(null);
}

/**
 * Fetches the browser's half of the security configuration once. The route table it carries is the very same
 * one the API enforces, so a request is signed exactly when the server will ask for a signature and neither
 * side can be taught about a protected route the other has not heard of.
 *
 * A failure is not cached: reCAPTCHA stays off for that attempt, the API refuses the write on its own terms,
 * and the next call tries again rather than leaving the client permanently unarmed.
 */
export function ensureClientConfiguration(): Promise<void> {
  if (configurationLoaded) return Promise.resolve();
  if (configurationPromise !== undefined) return configurationPromise;

  configurationPromise = fetch(`${API_BASE}/client-configuration`, {
    method: 'GET',
    headers: createHeaders({}),
    credentials: 'include',
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`Client configuration failed with status ${response.status}.`);
      const payload = await response.json() as ClientConfiguration;
      const recaptcha = payload.recaptcha;
      const enabled = !recaptchaDisabled && recaptcha?.enabled === true
        && typeof recaptcha.siteKey === 'string' && typeof recaptcha.scriptHost === 'string';

      configureRecaptcha(enabled
        ? { siteKey: recaptcha.siteKey as string, scriptHost: recaptcha.scriptHost as string }
        : null);
      protectedRoutes = enabled ? recaptcha.protectedRoutes ?? [] : [];
      configurationLoaded = true;
      preloadRecaptcha();
    })
    .catch(() => {
      configureRecaptcha(null);
      protectedRoutes = [];
    })
    .finally(() => {
      configurationPromise = undefined;
    });

  return configurationPromise;
}

/** Drops the cached configuration so the next request fetches it again. Tests use it; the application does not. */
export function clearClientConfiguration(): void {
  protectedRoutes = [];
  configurationPromise = undefined;
  configurationLoaded = false;
  recaptchaDisabled = false;
  configureRecaptcha(null);
}

/** Compares the request against the published table. A query string and a trailing slash never change the route. */
function findRecaptchaAction(method: string, path: string): string | undefined {
  const requested = `${API_BASE}${path.split('?')[0]}`.replace(/\/+$/u, '');
  return protectedRoutes.find((route) =>
    route.method.toUpperCase() === method.toUpperCase()
    && route.path.replace(/\/+$/u, '').toLowerCase() === requested.toLowerCase())?.action;
}

/**
 * Signs a protected write with a fresh reCAPTCHA token. Tokens are single-use and expire in two minutes, so
 * one is minted per request and never reused.
 */
async function applyRecaptchaToken(headers: Headers, method: string, path: string): Promise<void> {
  await ensureClientConfiguration();
  const action = findRecaptchaAction(method, path);
  if (action === undefined) return;

  const token = await executeRecaptcha(action);
  if (token !== null) headers.set(RECAPTCHA_HEADER, token);
}

/**
 * The one place a request actually leaves the browser, and therefore the one place that knows
 * whether the server is reachable.
 *
 * `navigator.onLine` cannot answer that — it reports a network interface, not a server — so the
 * workspace's view of the connection is corrected here, by what happened rather than by what the
 * device believes. Everything that reads `isOnline()` depends on this being the only door out.
 */
async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    const response = await fetch(url, init);
    markServerReachable();
    return response;
  } catch (error) {
    // A rejected `fetch` means nothing came back at all: no network, no DNS, no server. An HTTP
    // error, however unhappy, proves the opposite and is left to the caller.
    if (isNetworkFailure(error)) markServerUnreachable();
    throw error;
  }
}

/**
 * Sends a request to the API, with no offline layer in the way.
 *
 * The sync engine replays queued writes through this rather than through `apiRequest`, because a
 * replay must never be offered back to the very queue it came out of.
 */
export async function apiRequestDirect<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = createHeaders(init);
  const method = init.method ?? 'GET';
  if (isMutation(method)) {
    headers.set('X-CSRF-TOKEN', await ensureCsrfToken());
    await applyRecaptchaToken(headers, method, path);
  }

  const response = await send(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });

  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiRequestError(
      response.status,
      problem?.detail ?? problem?.title ?? `API request failed with status ${response.status}.`,
      problem?.code,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * The request every feature makes.
 *
 * When no offline layer is installed — a browser without Web Crypto, or an operator who has not
 * turned the offline copy on — this is `apiRequestDirect` with one extra function call. When one
 * is installed, it is the offline layer that decides whether a read is answered from the device
 * and whether a write is captured for later, because those are policy questions and this module
 * is transport.
 */
export function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const runtime = offlineRequestRuntime();
  if (runtime === null) return apiRequestDirect<T>(path, init);

  const method = init.method ?? 'GET';
  if (isMutation(method)) {
    const parsedBody = typeof init.body === 'string' ? JSON.parse(init.body) as unknown : undefined;
    return runtime.interceptWrite<T>(method, path, parsedBody) ?? apiRequestDirect<T>(path, init);
  }

  return runtime.interceptRead<T>(path, () => apiRequestDirect<T>(path, init)) ?? apiRequestDirect<T>(path, init);
}

export async function apiBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const headers = createHeaders(init);
  const method = init.method ?? 'GET';
  // Signed on the same terms as apiRequest, so a route added to the protected table is covered whichever
  // transport happens to call it.
  if (isMutation(method)) {
    headers.set('X-CSRF-TOKEN', await ensureCsrfToken());
    await applyRecaptchaToken(headers, method, path);
  }
  const response = await send(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });
  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiRequestError(
      response.status,
      problem?.detail ?? problem?.title ?? `API request failed with status ${response.status}.`,
      problem?.code,
    );
  }
  return response.blob();
}

export interface ApiStreamEvent {
  event: string;
  data: unknown;
}

/**
 * Reads a POST response as server-sent events while retaining the same cookie, CSRF, correlation,
 * locale, and reCAPTCHA protections as every other API write. Streaming requests deliberately bypass
 * the offline queue: an AI run must never be replayed later as an ordinary background mutation.
 */
export async function apiEventStream<T>(
  path: string,
  init: RequestInit = {},
  onEvent?: (event: ApiStreamEvent) => void,
): Promise<T> {
  const headers = createHeaders(init);
  const method = init.method ?? 'POST';
  headers.set('Accept', 'text/event-stream');
  if (isMutation(method)) {
    headers.set('X-CSRF-TOKEN', await ensureCsrfToken());
    await applyRecaptchaToken(headers, method, path);
  }

  const response = await send(`${API_BASE}${path}`, { ...init, method, headers, credentials: 'include' });
  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiRequestError(
      response.status,
      problem?.detail ?? problem?.title ?? `API request failed with status ${response.status}.`,
      problem?.code,
    );
  }
  if (response.body === null) throw new ApiRequestError(502, 'The API returned an empty event stream.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: T | undefined;
  let hasResult = false;

  const consume = (block: string): void => {
    let eventName = 'message';
    const data: string[] = [];
    for (const line of block.split(/\r?\n/u)) {
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
    }
    if (data.length === 0) return;
    const payload = JSON.parse(data.join('\n')) as unknown;
    onEvent?.({ event: eventName, data: payload });
    if (eventName === 'message') {
      result = payload as T;
      hasResult = true;
    } else if (eventName === 'error') {
      const problem = payload as ProblemDetails & { status?: number };
      throw new ApiRequestError(
        problem.status ?? 422,
        problem.detail ?? problem.title ?? 'The event stream failed.',
        problem.code,
      );
    }
  };

  while (true) {
    const next = await reader.read();
    buffer += decoder.decode(next.value, { stream: !next.done });
    let separator = /\r?\n\r?\n/u.exec(buffer);
    while (separator?.index !== undefined) {
      consume(buffer.slice(0, separator.index));
      buffer = buffer.slice(separator.index + separator[0].length);
      separator = /\r?\n\r?\n/u.exec(buffer);
    }
    if (next.done) break;
  }
  if (buffer.trim().length > 0) consume(buffer);
  if (!hasResult) throw new ApiRequestError(502, 'The event stream ended before a message was returned.');
  return result as T;
}
